import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Settings2, Pencil } from 'lucide-react';
import { ROOM_TYPES, getProjectTypes } from '@/components/widget/config/roomOptions';
import { AreaConfigModal } from './AreaConfigModal';
import type { RoomType, RoomConfig, RoomOverride } from '@/types/firestore';

interface RoomConfigSettingsProps {
  roomConfig: RoomConfig | undefined;
  onChange: (config: RoomConfig) => void;
}

export function RoomConfigSettings({ roomConfig, onChange }: RoomConfigSettingsProps) {
  const [editingRoom, setEditingRoom] = useState<RoomType | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configModalRoom, setConfigModalRoom] = useState<RoomType | null>(null);
  const [roomOverrideForm, setRoomOverrideForm] = useState<RoomOverride>({
    label: '',
    description: '',
  });

  // Get current enabled rooms (default to all if not configured)
  const enabledRooms = roomConfig?.enabled_rooms ?? ROOM_TYPES.map((r) => r.id);
  const roomOverrides = roomConfig?.room_overrides ?? {};

  // Toggle room enabled/disabled
  const toggleRoom = (roomId: RoomType) => {
    const newEnabled = enabledRooms.includes(roomId)
      ? enabledRooms.filter((r) => r !== roomId)
      : [...enabledRooms, roomId];

    onChange({
      ...roomConfig,
      enabled_rooms: newEnabled,
      room_overrides: roomOverrides,
    });
  };

  // Open edit dialog for a room (label/description only)
  const openEditDialog = (roomId: RoomType) => {
    setEditingRoom(roomId);
    const currentOverride = roomOverrides[roomId];
    setRoomOverrideForm({
      label: currentOverride?.label ?? '',
      description: currentOverride?.description ?? '',
    });
    setEditDialogOpen(true);
  };

  // Save room override (label/description only)
  const saveRoomOverride = () => {
    if (!editingRoom) return;

    const newOverrides = { ...roomOverrides };
    const existingOverride = newOverrides[editingRoom] || {};

    // Only save if there are actual overrides
    if (roomOverrideForm.label || roomOverrideForm.description) {
      newOverrides[editingRoom] = {
        ...existingOverride,
        label: roomOverrideForm.label || undefined,
        description: roomOverrideForm.description || undefined,
      };
    } else if (existingOverride.projectTypes || existingOverride.conversion_options) {
      // Keep project types but remove label/description
      newOverrides[editingRoom] = {
        ...existingOverride,
        label: undefined,
        description: undefined,
      };
    } else {
      // Remove override if nothing left
      delete newOverrides[editingRoom];
    }

    onChange({
      ...roomConfig,
      enabled_rooms: enabledRooms,
      room_overrides: newOverrides,
    });

    setEditDialogOpen(false);
    setEditingRoom(null);
  };

  // Open configuration modal for a room
  const openConfigModal = (roomId: RoomType) => {
    setConfigModalRoom(roomId);
    setConfigModalOpen(true);
  };

  // Save configuration from modal
  const saveAreaConfig = (override: RoomOverride) => {
    if (!configModalRoom) return;

    const newOverrides = { ...roomOverrides };

    // Check if override has any meaningful content
    const hasContent =
      override.label ||
      override.description ||
      (override.projectTypes && override.projectTypes.length > 0) ||
      (override.conversion_options && override.conversion_options.length > 0);

    if (hasContent) {
      newOverrides[configModalRoom] = override;
    } else {
      delete newOverrides[configModalRoom];
    }

    onChange({
      ...roomConfig,
      enabled_rooms: enabledRooms,
      room_overrides: newOverrides,
    });
  };

  // Get display info for a room
  const getRoomDisplayInfo = (roomId: RoomType) => {
    const defaultRoom = ROOM_TYPES.find((r) => r.id === roomId);
    const override = roomOverrides[roomId];
    const projectTypes = getProjectTypes(roomId, roomConfig);

    return {
      label: override?.label || defaultRoom?.label || roomId,
      description: override?.description || defaultRoom?.description || '',
      hasLabelOverride: !!override?.label,
      hasDescOverride: !!override?.description,
      hasProjectTypes: projectTypes !== null && projectTypes.length > 0,
      projectTypeCount: projectTypes?.length || 0,
      isCustomConfig: !!(override?.projectTypes && override.projectTypes.length > 0),
    };
  };

  // Check if room type supports project types
  const supportsProjectTypes = (roomId: RoomType): boolean => {
    // These room types can have project types configured
    return ['outdoor', 'garage', 'basement'].includes(roomId);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Area Configuration</CardTitle>
        <CardDescription>
          Configure which areas are available in your widget and customize their project types
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enabled Rooms Grid */}
        <div className="space-y-4">
          <Label className="text-sm font-medium">Available Areas</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ROOM_TYPES.map((room) => {
              const isEnabled = enabledRooms.includes(room.id);
              const info = getRoomDisplayInfo(room.id);
              const hasSupport = supportsProjectTypes(room.id);

              return (
                <div
                  key={room.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                    isEnabled
                      ? 'bg-card hover:bg-muted/30'
                      : 'bg-muted/20 opacity-60'
                  }`}
                >
                  <Checkbox
                    id={`room-${room.id}`}
                    checked={isEnabled}
                    onCheckedChange={() => toggleRoom(room.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor={`room-${room.id}`}
                      className="text-sm font-medium cursor-pointer flex items-center gap-2 flex-wrap"
                    >
                      {info.label}
                      {(info.hasLabelOverride || info.hasDescOverride) && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          Customized
                        </Badge>
                      )}
                      {info.hasProjectTypes && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {info.projectTypeCount} types
                        </Badge>
                      )}
                    </label>
                    <p className="text-xs text-muted-foreground truncate">{info.description}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {hasSupport && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openConfigModal(room.id)}
                        title="Configure project types"
                      >
                        <Settings2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEditDialog(room.id)}
                      title="Edit label and description"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Info about project types */}
        <div className="text-xs text-muted-foreground border-t pt-4">
          <p>
            <strong>Tip:</strong> Click the{' '}
            <Settings2 className="h-3 w-3 inline-block mx-0.5" /> button on Outdoor, Garage, or
            Basement to configure project types (e.g., Deck, Patio, Pergola for Outdoor).
          </p>
        </div>
      </CardContent>

      {/* Edit Label/Description Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Customize Area</DialogTitle>
            <DialogDescription>
              Override the default label and description for this area type
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="room-label">Custom Label</Label>
              <Input
                id="room-label"
                placeholder={
                  ROOM_TYPES.find((r) => r.id === editingRoom)?.label || 'Room label'
                }
                value={roomOverrideForm.label || ''}
                onChange={(e) =>
                  setRoomOverrideForm((prev) => ({ ...prev, label: e.target.value }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use the default: "
                {ROOM_TYPES.find((r) => r.id === editingRoom)?.label}"
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="room-description">Custom Description</Label>
              <Input
                id="room-description"
                placeholder={
                  ROOM_TYPES.find((r) => r.id === editingRoom)?.description || 'Room description'
                }
                value={roomOverrideForm.description || ''}
                onChange={(e) =>
                  setRoomOverrideForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use the default: "
                {ROOM_TYPES.find((r) => r.id === editingRoom)?.description}"
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveRoomOverride}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Area Configuration Modal */}
      {configModalRoom && (
        <AreaConfigModal
          open={configModalOpen}
          onOpenChange={setConfigModalOpen}
          roomType={configModalRoom}
          roomConfig={roomConfig}
          onSave={saveAreaConfig}
        />
      )}
    </Card>
  );
}
